// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import InfoCard from "@conklin/App/Components/InfoCards/InfoCard";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";

interface InfoCardFields {
    fields: {
        title: string;
        titleLink: LinkFieldValue;
        titleSizeVariant: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
        subtitle: string;
        textContent: string;
        image: string;
        imageCaption: string;
        imagePosition: string;
        buttonLabel: string;
        buttonLink: LinkFieldValue;
        subtitleUrl: LinkFieldValue;
        buttonLinkSize: "small" | "large";
        buttonAlignment: "left" | "right" | "center";
        openInNewTab: boolean;
    };
}

const enum fields {
    cards = "cards",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.cards]: InfoCardFields[];
    };
}

type Props = OwnProps;

interface InfoCardsStyles {
    container?: InjectableCss;
    content?: InjectableCss;
}

export const infoCardsStyles: InfoCardsStyles = {};

const InfoCards: React.FC<Props> = ({ fields }) => {
    if (!fields.cards || fields.cards.length === 0) {
        return null;
    }

    const styles = infoCardsStyles;
    const styledProps = { numberOfCards: fields.cards.length };

    return (
        <StyledWrapper {...styles.container} data-test-selector="info-cards-widget">
            {fields.cards.map((card, index) => (
                <StyledWrapper {...styles.content} {...styledProps} key={`${card.fields.title}-${index}`}>
                    <InfoCard
                        buttonLabel={card.fields.buttonLabel}
                        title={card.fields.title}
                        titleSizeVariant={card.fields.titleSizeVariant}
                        subtitle={card.fields.subtitle}
                        subtitleUrl={card.fields.subtitleUrl}
                        textContent={card.fields.textContent}
                        buttonLink={card.fields.buttonLink}
                        buttonLinkSize={card.fields.buttonLinkSize}
                        image={card.fields.image}
                        imageCaption={card.fields.imageCaption}
                        imagePosition={card.fields.imagePosition}
                        buttonAlignment={card.fields.buttonAlignment}
                        titleLink={card.fields.titleLink}
                        openInNewTab={card.fields.openInNewTab}
                    />
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: InfoCards,
    definition: {
        group: "XNGAGE_CUSTOM_WIDGET" as any,
        fieldDefinitions: [
            {
                name: fields.cards,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title || "Conklin Info Card";
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "image",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "imageCaption",
                        displayName: "Image Caption",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                        isVisible: item => item.fields.image,
                    },
                    {
                        name: "imagePosition",
                        displayName: "Image Position",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Left", value: "row" },
                            { displayName: "Right", value: "row-reverse" },
                            { displayName: "top", value: "column" },
                            { displayName: "bottom", value: "column-reverse" },
                        ],
                        defaultValue: "row",
                        isVisible: item => item.fields.image,
                    },
                    {
                        name: "subtitle",
                        displayName: "Subtitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                    },
                    {
                        name: "subtitleUrl",
                        displayName: "Subtitle Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "title",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                    },
                    {
                        name: "titleLink",
                        displayName: "Title Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "titleSizeVariant",
                        editorTemplate: "DropDownField",
                        displayName: "Title Variant",
                        options: [
                            { value: "h1", displayName: "h1" },
                            { value: "h2", displayName: "h2" },
                            { value: "h3", displayName: "h3" },
                            { value: "h4", displayName: "h4" },
                            { value: "h5", displayName: "h5" },
                            { value: "h6", displayName: "h6" },
                        ],
                        defaultValue: "h4",
                        isRequired: true,
                    },
                    {
                        name: "textContent",
                        displayName: "Copy",
                        editorTemplate: "MultilineTextField",
                        defaultValue: "",
                    },
                    {
                        name: "buttonLabel",
                        displayName: "Button Text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "buttonLink",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isRequired: true,
                    },
                    {
                        name: "buttonAlignment",
                        displayName: "Button Alignment",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Left", value: "left" },
                            { displayName: "Right", value: "right" },
                            { displayName: "center", value: "center" },
                        ],
                        defaultValue: "left",
                    },
                    {
                        name: "buttonLinkSize",
                        displayName: "Link Size",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Small", value: "small" },
                            { displayName: "Large", value: "large" },
                        ],
                        defaultValue: "small",
                    },
                    {
                        name: "openInNewTab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
